/**
 * Independent components
 * These components do not include any other components
 *
 * This order mitigates problems that might arise when trying to import components
 * that have circular dependencies to other components.
 * Note: import-order also affects to the generated CSS bundle file.
 *
 * Read more:
 * https://medium.com/visual-development/how-to-fix-nasty-circular-dependency-issues-once-and-for-all-in-javascript-typescript-a04c987cf0de
 */

import loadable from '@loadable/component';
import { loadableField } from './FieldLoading/FieldLoading';

// Icons
export { default as Icon } from './Icon/Icon';
export { default as IconCard } from './IconCard/IconCard';
// Loaded with the page that uses it, so its styles and rc-tooltip stay out of the shared bundle
export const InfoTooltip = loadable(() =>
  import(/* webpackChunkName: "InfoTooltip" */ './InfoTooltip/InfoTooltip')
);
// Loaded with the page that uses it (it brings FilterChip's styles)
export const FilterDropdown = loadable(() =>
  import(/* webpackChunkName: "FilterDropdown" */ './FilterDropdown/FilterDropdown')
);
export { default as ProductionStageArc } from './ProductionStageArc/ProductionStageArc';
export { default as IconSocialMediaInstagram } from './IconSocialMediaInstagram/IconSocialMediaInstagram';
export { default as IconSocialMediaX } from './IconSocialMediaX/IconSocialMediaX';
export { default as IconSpinner } from './IconSpinner/IconSpinner';

// Other independent components
export { default as ExternalLink } from './ExternalLink/ExternalLink';
export { default as ExpandingTextarea } from './ExpandingTextarea/ExpandingTextarea';
export { default as Form } from './Form/Form';
export { default as LimitedAccessBanner } from './LimitedAccessBanner/LimitedAccessBanner';
export { default as Logo } from './Logo/Logo';
export { default as AssignmentEndedNotice } from './AssignmentEndedNotice/AssignmentEndedNotice';
export { default as NamedLink } from './NamedLink/NamedLink';
export { default as NamedRedirect } from './NamedRedirect/NamedRedirect';
export { default as OutsideClickHandler } from './OutsideClickHandler/OutsideClickHandler';
// In the app shell (Step 3, piece 14), so always loaded: keep it small
export { default as OfflineBanner } from './OfflineBanner/OfflineBanner';
export { default as Promised } from './Promised/Promised';
export { default as ResponsiveImage } from './ResponsiveImage/ResponsiveImage';
export { default as TimeRange } from './TimeRange/TimeRange';
export { default as UserDisplayName } from './UserDisplayName/UserDisplayName';
export { default as ValidationError } from './ValidationError/ValidationError';

/**
 * Composite components
 * These components include other components
 */

//////////////////////////////////////////////////////////
// First components that include only atomic components //
//////////////////////////////////////////////////////////

export { default as Button, PrimaryButton, PrimaryButtonInline, SecondaryButton, SecondaryButtonInline, DangerButton, InlineTextButton, SocialLoginButton } from './Button/Button';
export { default as CookieConsent } from './CookieConsent/CookieConsent';
export { default as OrgProfileWizardModal } from './OrgProfileWizardModal/OrgProfileWizardModal';
export { default as CreatorProfileWizardModal } from './CreatorProfileWizardModal/CreatorProfileWizardModal';
export const ImageCarousel = loadable(() => import(/* webpackChunkName: "ImageCarousel" */ './ImageCarousel/ImageCarousel'));
export { default as ListingLink } from './ListingLink/ListingLink';
export { default as PaginationLinks } from './PaginationLinks/PaginationLinks';

// Layout helpers
export { default as LayoutWrapperFooter } from './LayoutWrapperFooter/LayoutWrapperFooter';
export { default as LayoutWrapperMain } from './LayoutWrapperMain/LayoutWrapperMain';
export { default as LayoutWrapperSideNav } from './LayoutWrapperSideNav/LayoutWrapperSideNav';
export { default as LayoutWrapperTopbar } from './LayoutWrapperTopbar/LayoutWrapperTopbar';
export { default as LayoutSideNavigation } from './LayoutSideNavigation/LayoutSideNavigation';
export { default as LayoutSingleColumn } from './LayoutSingleColumn/LayoutSingleColumn';

// Menu
export { default as MenuItem } from './MenuItem/MenuItem';
export { default as MenuAction } from './MenuAction/MenuAction';
export { default as MenuContent } from './MenuContent/MenuContent';
export { default as MenuLabel } from './MenuLabel/MenuLabel';
export { default as Menu } from './Menu/Menu';
export { default as RowActionsMenu } from './RowActionsMenu/RowActionsMenu';
export { default as IconButton } from './IconButton/IconButton';
export { default as ButtonGroup } from './ButtonGroup/ButtonGroup';
export { default as AddMembersToListModal } from './AddMembersToListModal/AddMembersToListModal';
export { default as RenameListModal } from './RenameListModal/RenameListModal';

// Dashboard tables (Step 3, piece 24): loaded with the page that uses them, so their styles stay
// out of the shared bundle. TableHeader…TableRowItem are the hand-built mode, until its pages move
// to `columns`.
export const Table = loadable(() => import(/* webpackChunkName: "Table" */ './Table/Table'));
export const TableBulkBar = loadable(() =>
  import(/* webpackChunkName: "Table" */ './TableBulkBar/TableBulkBar')
);
export const TablePagination = loadable(() =>
  import(/* webpackChunkName: "Table" */ './TablePagination/TablePagination')
);
export const TableHeader = loadable(() =>
  import(/* webpackChunkName: "Table" */ './Table/TableHeader')
);
export const TableHeadings = loadable(() =>
  import(/* webpackChunkName: "Table" */ './Table/TableHeadings')
);
export const TableHeading = loadable(() =>
  import(/* webpackChunkName: "Table" */ './Table/TableHeading')
);
export const TableContent = loadable(() =>
  import(/* webpackChunkName: "Table" */ './Table/TableContent')
);
export const TableRow = loadable(() => import(/* webpackChunkName: "Table" */ './Table/TableRow'));
export const TableRowItem = loadable(() =>
  import(/* webpackChunkName: "Table" */ './Table/TableRowItem')
);

// Auth / role gating

// Modal
export { default as Modal } from './Modal/Modal';
export { default as ModalInMobile } from './ModalInMobile/ModalInMobile';
export { default as CalendarEventModal } from './CalendarEventModal/CalendarEventModal';
export { default as RsvpAvatarStack } from './RsvpAvatarStack/RsvpAvatarStack';
export { default as ConsentBasisFields, attestationFromValues } from './ConsentBasisFields/ConsentBasisFields';
export { default as CreateEditEventModal } from './CreateEditEventModal/CreateEditEventModal';

///////////////////////////////////////////////
// These components include other components //
///////////////////////////////////////////////

export { default as Avatar, AvatarSmall, AvatarMedium, AvatarLarge } from './Avatar/Avatar';
export { default as PaymentBreakdown } from './PaymentBreakdown/PaymentBreakdown';
export { default as BookingBreakdown } from './BookingBreakdown/BookingBreakdown';
export { default as BookingDateRangeFilter } from './BookingDateRangeFilter/BookingDateRangeFilter';
export { default as BookingPanel } from './BookingPanel/BookingPanel';
export { default as ShowPanel } from './ShowPanel/ShowPanel';
export { default as FilterPlain } from './FilterPlain/FilterPlain';
export { default as FilterPopup } from './FilterPopup/FilterPopup';
export { default as QuantityFilter } from './QuantityFilter/QuantityFilter';
export { default as ListingCard } from './ListingCard/ListingCard';
export { default as LiveRoomCard } from './LiveRoomCard/LiveRoomCard';
// Feedback (Step 3, piece 12): loaded with the page that uses them, so their styles stay out of
// the shared bundle
export const Alert = loadable(() => import(/* webpackChunkName: "Alert" */ './Alert/Alert'));
export const EmptyState = loadable(() =>
  import(/* webpackChunkName: "EmptyState" */ './EmptyState/EmptyState')
);
// The dashboard's 403 (Step 3, piece 14): loaded with the dashboard page that shows it
export const ForbiddenPage = loadable(() =>
  import(/* webpackChunkName: "ForbiddenPage" */ './ForbiddenPage/ForbiddenPage')
);
export { default as Page } from './Page/Page';
export { default as Reviews, Review } from './Reviews/Reviews';
export { default as SavedCardDetails } from './SavedCardDetails/SavedCardDetails';
export { default as StripeConnectAccountStatusBox } from './StripeConnectAccountStatusBox/StripeConnectAccountStatusBox';
export { default as StripePaymentAddress } from './StripePaymentAddress/StripePaymentAddress';
export { default as UserCard } from './UserCard/UserCard';
// Loading placeholders (Step 3, piece 12): loaded with the page that uses them, so their styles
// stay out of the shared bundle
export const Skeleton = loadable(() =>
  import(/* webpackChunkName: "Skeleton" */ './Skeleton/Skeleton')
);
export const UserSearchInput = loadableField(() =>
  import(/* webpackChunkName: "UserSearchInput" */ './UserSearchInput/UserSearchInput')
);

// Fields (for Final Form). A field in its own chunk shows FieldLoading, its label and an empty
// box of its size, until the chunk is in (loadableField).
export { default as FieldLoading } from './FieldLoading/FieldLoading';
export { default as FieldBirthdayInput } from './FieldBirthdayInput/FieldBirthdayInput';
export { default as FieldCheckbox } from './FieldCheckbox/FieldCheckbox';
export { default as FieldCurrencyInput } from './FieldCurrencyInput/FieldCurrencyInput';
// Loaded with the page that uses it, so its styles stay out of the shared bundle
export const FieldDropzone = loadable(() =>
  import(/* webpackChunkName: "FieldDropzone" */ './FieldDropzone/FieldDropzone')
);
// Heavy components (the template's date pickers, and those that bring a large library:
// add-to-calendar, phone input, Lexical editor, image gallery) load in their own chunk
// where a page uses them, so they stay out of the bundle every page loads.
export const FieldDateRangePicker = loadableField(() => import(/* webpackChunkName: "FieldDateRangePicker" */ './DatePicker/FieldDateRangePicker/FieldDateRangePicker'));
export const FieldSingleDatePicker = loadableField(() => import(/* webpackChunkName: "FieldSingleDatePicker" */ './DatePicker/FieldSingleDatePicker/FieldSingleDatePicker'));
export const FieldDateRangeController = loadable(() => import(/* webpackChunkName: "FieldDateRangeController" */ './DatePicker/FieldDateRangeController/FieldDateRangeController'));
export { default as FieldRadioButton } from './FieldRadioButton/FieldRadioButton';
export const FieldOptionCard = loadable(() =>
  import(/* webpackChunkName: "FieldOptionCard" */ './FieldOptionCard/FieldOptionCard')
);
// Loaded with the page that uses it, so its styles stay out of the shared bundle
export const FieldReviewRating = loadable(() =>
  import(/* webpackChunkName: "FieldReviewRating" */ './FieldReviewRating/FieldReviewRating')
);
export { default as FieldSelect } from './FieldSelect/FieldSelect';
export { default as FieldTextInput } from './FieldTextInput/FieldTextInput';
export const FieldRichTextInput = loadable(() => import(/* webpackChunkName: "FieldRichTextInput" */ './FieldRichTextInput/FieldRichTextInput'));
export { default as FieldPasswordInput } from './FieldPasswordInput/FieldPasswordInput';
export { default as FieldHelp } from './FieldHelp/FieldHelp';
export { default as SearchInput } from './SearchInput/SearchInput';
export { default as SegmentedControl } from './SegmentedControl/SegmentedControl';
export { default as LanguagePill } from './LanguagePill/LanguagePill';
// Loaded with the page that uses it, so its styles stay out of the shared bundle
export const FilterChip = loadable(() =>
  import(/* webpackChunkName: "FilterChip" */ './FilterChip/FilterChip')
);
export const FilterChipOption = loadable(
  () => import(/* webpackChunkName: "FilterChip" */ './FilterChip/FilterChip'),
  { resolveComponent: module => module.FilterChipOption }
);
// Fields that use other Fields
export const FieldMultiSelectInput = loadableField(() => import(/* webpackChunkName: "FieldMultiSelectInput" */ './FieldMultiSelectInput/FieldMultiSelectInput'));
export { default as FieldTimeZoneSelect } from './FieldTimeZoneSelect/FieldTimeZoneSelect';
// Loaded with the page that uses it, so its styles stay out of the shared bundle
export const FieldCheckboxGroup = loadable(() =>
  import(/* webpackChunkName: "FieldCheckboxGroup" */ './FieldCheckboxGroup/FieldCheckboxGroup')
);
export const FieldPhoneNumberInput = loadableField(() => import(/* webpackChunkName: "FieldPhoneNumberInput" */ './FieldPhoneNumberInput/FieldPhoneNumberInput'));
// Fields and inputs using old naming pattern
export { LocationAutocompleteInputField } from './LocationAutocompleteInput/LocationAutocompleteInput';

// Tab navigation
export { default as TabNav } from './TabNav/TabNav';
export {
  LinkTabNavHorizontal,
  ButtonTabNavHorizontal,
  TabPanel,
} from './TabNavHorizontal/TabNavHorizontal';
export { default as UserNav } from './UserNav/UserNav';
export { default as DashboardNav } from './DashboardNav/DashboardNav';


//////////////////////////////////////////////
// Page sections and modal content wrappers //
//////////////////////////////////////////////

export { default as LayoutWrapperAccountSettingsSideNav } from './LayoutWrapperAccountSettingsSideNav/LayoutWrapperAccountSettingsSideNav';
// The 500 page: always loaded, so it shows even when code chunks can't load
export { default as ErrorPage } from './ErrorPage/ErrorPage';
export { default as LoadableComponentErrorBoundary } from './LoadableComponentErrorBoundary/LoadableComponentErrorBoundary'
export { default as ShowReviewModal } from './ShowReviewModal/ShowReviewModal';
export { default as ShowRatingModal } from './ShowRatingModal/ShowRatingModal';
export { default as ShareModal } from './ShareModal/ShareModal';
export { default as NewsletterPromptModal } from './NewsletterPromptModal/NewsletterPromptModal';
export { default as BehindTheScenesUploadModal } from './BehindTheScenesUploadModal/BehindTheScenesUploadModal';
export { default as MembershipModal, UpgradeMembershipModal, FreeMembershipModal } from './MembershipModal/MembershipModal';   
export { default as DonateModal } from './DonateModal/DonateModal';
export { default as PointsModal } from './PointsModal/PointsModal';
export { default as PrivacyPolicy } from './PrivacyPolicy/PrivacyPolicy';
export { default as TermsOfService } from './TermsOfService/TermsOfService';
export { default as EditListingBasicInfoPanel } from './EditListingBasicInfoPanel/EditListingBasicInfoPanel';
export { default as EditListingGalleryPanel } from './EditListingGalleryPanel/EditListingGalleryPanel';
export { default as EditListingTeamPanel } from './EditListingTeamPanel/EditListingTeamPanel';
export { default as EditListingTouringLinksPanel } from './EditListingTouringLinksPanel/EditListingTouringLinksPanel';
export { default as EditListingWizard } from './EditListingWizard/EditListingWizard';
export { default as EditLiveRoomDetailsPanel } from './EditLiveRoomDetailsPanel/EditLiveRoomDetailsPanel';
export { default as EditLiveRoomGalleryPanel } from './EditLiveRoomGalleryPanel/EditLiveRoomGalleryPanel';
export { default as EditLiveRoomBehindTheScenesPanel } from './EditLiveRoomBehindTheScenesPanel/EditLiveRoomBehindTheScenesPanel';
export { default as EditLiveRoomMembershipPanel } from './EditLiveRoomMembershipPanel/EditLiveRoomMembershipPanel';
export { default as EditLiveRoomTeamPanel } from './EditLiveRoomTeamPanel/EditLiveRoomTeamPanel';
export { default as EditLiveRoomSchedulesPanel } from './EditLiveRoomSchedulesPanel/EditLiveRoomSchedulesPanel';
export { default as EditLiveRoomWizard } from './EditLiveRoomWizard/EditLiveRoomWizard';
export { default as Footer } from './Footer/Footer';
export { default as SectionHero } from './SectionHero/SectionHero';
export { default as SectionListings } from './SectionListings/SectionListings';
export { default as SectionLiveRooms } from './SectionLiveRooms/SectionLiveRooms';
export { default as SectionWatchlistListings } from './SectionWatchlistListings/SectionWatchlistListings';
export { default as SectionJoin } from './SectionJoin/SectionJoin';
export { default as Topbar } from './Topbar/Topbar';
export { default as TopbarDesktop } from './TopbarDesktop/TopbarDesktop';
export { default as TopbarMobileMenu } from './TopbarMobileMenu/TopbarMobileMenu';
export { default as VideoPlayer } from './VideoPlayer/VideoPlayer';
export { default as Slider } from './Slider/Slider';
export { default as ExpandableBio } from './ExpandableBio/ExpandableBio';
export { default as ImageFromFile } from './ImageFromFile/ImageFromFile';
export { default as VideoFromFile } from './VideoFromFile/VideoFromFile';
export { default as ScrollableNav } from './ScrollableNav/ScrollableNav';
export const AddToCalendar = loadable(() => import(/* webpackChunkName: "AddToCalendar" */ './AddToCalendar/AddToCalendar'));
export { default as DashboardLayout, DashboardWrapper } from './DashboardLayout/DashboardLayout';
export { default as SettingsLayout } from './SettingsLayout/SettingsLayout';
export { default as SettingsAccordion } from './SettingsAccordion/SettingsAccordion';
export { default as Pill } from './Pill/Pill';
export { default as Counter } from './Counter/Counter';
// The phone dashboard's tab bar, loaded with the dashboard so its styles stay out of the shared
// bundle
export const DashboardTabBar = loadable(() =>
  import(/* webpackChunkName: "DashboardTabBar" */ './DashboardTabBar/DashboardTabBar')
);
export { default as GigBoostBadge } from './GigBoostBadge/GigBoostBadge';
export { default as RentedBadge } from './RentedBadge/RentedBadge';
export { default as PromotedBadge } from './PromotedBadge/PromotedBadge';
export { default as ProvenanceFilter } from './ProvenanceFilter/ProvenanceFilter';
export { default as Law25Banner } from './Law25Banner/Law25Banner';
export { default as DashboardLoader } from './DashboardLoader/DashboardLoader';
export { default as Toggle } from './Toggle/Toggle';
export { default as AspectRatioWrapper } from './AspectRatioWrapper/AspectRatioWrapper';
export { default as PreviewListingButton } from './PreviewListingButton/PreviewListingButton';
export { default as CommunityGuidelines } from './CommunityGuidelines/CommunityGuidelines';
export { default as InviteTeamMemberModal } from './InviteTeamMemberModal/InviteTeamMemberModal';
export { default as HorizontalUserCard, HorizontalNonExistingUserCard } from './HorizontalUserCard/HorizontalUserCard';
export { default as SectionRecentlyViewedListings } from './SectionRecentlyViewedListings/SectionRecentlyViewedListings';
export { default as Comments } from './Comments/Comments';
export { default as ConfirmationModal } from './ConfirmationModal/ConfirmationModal';
// Loaded with the page that uses it, so its styles stay out of the shared bundle
export const DashboardFlowModal = loadable(() =>
  import(/* webpackChunkName: "DashboardFlowModal" */ './DashboardFlowModal/DashboardFlowModal')
);
export const FieldConfirmWord = loadable(() =>
  import(/* webpackChunkName: "DashboardFlowModal" */ './DashboardFlowModal/FieldConfirmWord')
);
// The dashboard's plan box and plan notices (Step 3, piece 20): loaded where the dashboard shows
// them, so their styles stay out of the shared bundle. Paid plans are off in Phase 1 (plan 6p).
export const PlanUsageWidget = loadable(() =>
  import(/* webpackChunkName: "PlanUsageWidget" */ './PlanUsageWidget/PlanUsageWidget')
);
export const NoticeBanner = loadable(() =>
  import(/* webpackChunkName: "NoticeBanner" */ './NoticeBanner/NoticeBanner')
);
// The phone dashboard's top bar and the phone note, loaded with the dashboard so their styles stay
// out of the shared bundle
export const DashboardPhoneTopBar = loadable(() =>
  import(/* webpackChunkName: "DashboardPhone" */ './DashboardPhoneTopBar/DashboardPhoneTopBar')
);
export const DesktopOnlyNotice = loadable(() =>
  import(/* webpackChunkName: "DashboardPhone" */ './DesktopOnlyNotice/DesktopOnlyNotice')
);
export { default as HostCard } from './HostCard/HostCard';
export { default as SupportChatWidget } from './SupportChatWidget/SupportChatWidget';
export { default as Badges, BadgeStuff, BadgeBetaTester, BadgeTopReviewer, BadgeTier } from './Badges/Badges';
export { default as LanguageModal } from './LanguageModal/LanguageModal';
export { default as RecentlyViewedListingCard } from './RecentlyViewedListingCard/RecentlyViewedListingCard';
export { default as OrganizationProfileCard } from './OrganizationProfileCard/OrganizationProfileCard';
export { default as HorizontalListingCard } from './HorizontalListingCard/HorizontalListingCard';
export { default as ButtonWatchlist } from './ButtonWatchlist/ButtonWatchlist';
export { default as HeroListingCard } from './HeroListingCard/HeroListingCard';
export { default as ListingMembership } from './ListingMembership/ListingMembership';
export { default as ActionButton } from './ActionButton/ActionButton';

// Note: Conversations components (ChannelSidebar, MessageArea, etc.) are imported
// directly from their folders to avoid circular dependencies. Import them from:
// '../../components/Conversations/[ComponentName]/[ComponentName]'